import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandEmpty,
} from "../ui/command";
import { Label } from "../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { ChevronsUpDown } from "lucide-react";
import unauthenticatedApi from "@/utils/unauthenticatedAxios";
import { getRegionFromZipCode } from "@/utils/regionMapping";
import { formatRegionLabel } from "@/utils/regionLabel";

type RegionOption = { id: string; name: string; nameEn?: string | null };

type RegionFields = {
  zipCode: string;

  regionId: string | null;
  regionName: string;

  regionPathIds: string[];
  regionPathNames: string[];
};

export type RegionSelectorLabels = {
  zipLabel?: string;
  zipPlaceholder?: string;
  zipHint?: string;
  regionLabel?: string;
  regionPlaceholder?: string;
  regionHint?: string;
};

interface RegionSelectorProps<T extends RegionFields> {
  formData: T;
  setFormData: React.Dispatch<React.SetStateAction<T>>;
  openRegion: boolean;
  setOpenRegion: (open: boolean) => void;
  t: (key: string) => string;
  onZipCodeChange: (zipCode: string) => void;
  zipAutoEnabled?: boolean;
  labels?: RegionSelectorLabels;
}

export function RegionSelector<T extends RegionFields>({
  formData,
  setFormData,
  openRegion,
  setOpenRegion,
  t,
  onZipCodeChange,
  zipAutoEnabled = true,
  labels,
}: RegionSelectorProps<T>) {
  const requiredAsteriskStyle = { color: "var(--primary)" } as const;
  const renderRequiredLabel = (label: string) => {
    const starIndex = label.indexOf("*");
    if (starIndex === -1) return label;
    const before = label.slice(0, starIndex);
    const after = label.slice(starIndex + 1);
    return (
      <>
        {before}
        <span style={requiredAsteriskStyle}>*</span>
        {after}
      </>
    );
  };
  const REGION_API_URL = "/public/regions";
  const REGION_ZIP_URL = `${REGION_API_URL}/zip`;

  const mapOptions = (data: any): RegionOption[] => {
    const raw = Array.isArray(data) ? data : [];
    return raw
      .map((r: any) => ({
        id: String(r.id),
        name: String(r.name ?? ""),
        nameEn:
          typeof r.nameEn === "string" && r.nameEn.trim()
            ? String(r.nameEn)
            : null,
      }))
      .filter((r) => r.id.length > 0 && r.name.length > 0);
  };

  const [query, setQuery] = useState("");
  const [path, setPath] = useState<RegionOption[]>([]);
  const [options, setOptions] = useState<RegionOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (openRegion) return;

    const ids = Array.isArray(formData.regionPathIds)
      ? formData.regionPathIds
      : [];
    const names = Array.isArray(formData.regionPathNames)
      ? formData.regionPathNames
      : [];
    const length = Math.min(ids.length, names.length);

    if (length > 0) {
      setPath(ids.slice(0, length).map((id, index) => ({ id, name: names[index] })));
      return;
    }

    if (formData.regionId && formData.regionName) {
      setPath([{ id: formData.regionId, name: formData.regionName }]);
      return;
    }

    setPath([]);
  }, [
    formData.regionId,
    formData.regionName,
    formData.regionPathIds,
    formData.regionPathNames,
    openRegion,
  ]);

  const normalizeRegion = useCallback((value: string) => {
    return value
      .trim()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  }, []);

  const parentId = useMemo(() => {
    return path.length === 0 ? null : path[path.length - 1].id;
  }, [path]);

  const requestChildren = useCallback(async (pid: string | null) => {
    const res = await unauthenticatedApi.get(REGION_API_URL, {
      params: {
        ...(pid ? { parentId: pid } : {}),
      },
    });
    return mapOptions(res.data);
  }, []);

  useEffect(() => {
    if (!openRegion) return;
    setQuery("");
    setPath([]);
    setOptions([]);
    setFailed(false);
  }, [openRegion]);

  useEffect(() => {
    if (!openRegion) return;
    if (path.length === 0 && query.trim().length > 0) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setFailed(false);
      try {
        const children = await requestChildren(parentId);
        if (!cancelled) setOptions(children);
      } catch (err: any) {
        const status = err?.response?.status;
        if (!cancelled) {
          if (status === 404) {
            setOptions([]);
            setFailed(false);
          } else {
            setOptions([]);
            setFailed(true);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [openRegion, parentId, path.length, query, requestChildren]);

  useEffect(() => {
    if (!openRegion) return;
    if (path.length > 0) return;

    const trimmed = query.trim();
    if (trimmed.length === 0) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setFailed(false);
      try {
        const res = await unauthenticatedApi.get(REGION_API_URL, {
          params: { search: trimmed },
        });
        if (!cancelled) {
          setOptions(mapOptions(res.data));
        }
      } catch {
        if (!cancelled) {
          setOptions([]);
          setFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [openRegion, path.length, query, REGION_API_URL]);

  const breadcrumb = useMemo(() => {
    if (path.length === 0) return "";
    return path.map((p) => formatRegionLabel(p.name, p.nameEn)).join(" → ");
  }, [path]);

  const finalizeRegion = useCallback(
    (finalPath: RegionOption[] | null) => {
      if (!finalPath || finalPath.length === 0) {
        setFormData((prev) => ({
          ...prev,
          regionId: null,
          regionName: "",
          regionPathIds: [],
          regionPathNames: [],
        }));
        return;
      }

      const last = finalPath[finalPath.length - 1];
      const pathLabels = finalPath.map((entry) =>
        formatRegionLabel(entry.name, entry.nameEn)
      );
      setFormData((prev) => ({
        ...prev,
        regionId: last.id,
        regionName: formatRegionLabel(last.name, last.nameEn),
        regionPathIds: finalPath.map((p) => p.id),
        regionPathNames: pathLabels,
      }));
    },
    [setFormData]
  );

  const prevZipRef = useRef<string | null>(null);
  useEffect(() => {
    const currentZip = (formData.zipCode ?? "").trim();
    const prevZip = prevZipRef.current;

    // first render: just init ref
    if (prevZip === null) {
      prevZipRef.current = currentZip;
      return;
    }

    if (!zipAutoEnabled) {
      prevZipRef.current = currentZip;
      return;
    }

    // zipCode changed, clear selection and close popover
    if (currentZip !== prevZip) {
      finalizeRegion(null);
      setPath([]);
      setOptions([]);
      setQuery("");
      setFailed(false);
      setLoading(false);
      setOpenRegion(false);
      prevZipRef.current = currentZip;

      if (currentZip.length === 5) {
        unauthenticatedApi
          .get(REGION_ZIP_URL, { params: { zip: currentZip } })
          .then((res) => mapOptions(res.data))
          .then((zipPath) => {
            if (zipPath.length === 0) {
              const inferred = getRegionFromZipCode(currentZip);
              if (!inferred) return;
              return requestChildren(null).then((roots) => {
                const match = roots.find(
                  (option) =>
                    normalizeRegion(option.name) === normalizeRegion(inferred)
                );
                return match ? [match] : [];
              });
            }
            return zipPath;
          })
          .then((zipPath) => {
            if (!zipPath || zipPath.length === 0) return;
            setPath(zipPath);
            finalizeRegion(zipPath);
          })
          .catch(() => {
            // ignore lookup errors and leave region cleared
          });
      }
    }
  }, [
    formData.zipCode,
    finalizeRegion,
    requestChildren,
    setOpenRegion,
    normalizeRegion,
    REGION_ZIP_URL,
    zipAutoEnabled,
  ]);

  const handleBack = useCallback(() => {
    setPath((prev) => prev.slice(0, -1));
    setQuery("");
  }, []);

  // Done behaves like Unknown when at root and children exist.
  const handleDone = useCallback(() => {
    if (path.length === 0) {
      if (!formData.regionId) {
        finalizeRegion(null);
      }
      setOpenRegion(false);
      return;
    }

    // Sub-level commit current navigation path.
    finalizeRegion(path);
    setOpenRegion(false);
  }, [formData.regionId, finalizeRegion, path, setOpenRegion]);

  // Unknown behaves like Category dropdown.
  const handleUnknown = useCallback(() => {
    if (path.length === 0) {
      finalizeRegion(null);
      setPath([]);
    } else {
      finalizeRegion(path);
    }
    setQuery("");
    setOpenRegion(false);
  }, [finalizeRegion, path, setOpenRegion]);

  const handleClear = useCallback(() => {
    finalizeRegion(null);
    setPath([]);
    setQuery("");
    setOpenRegion(false);
  }, [finalizeRegion, setOpenRegion]);

  const handleSelect = useCallback(
    async (value: string) => {
      const selected = options.find((o) => o.id === value);
      if (!selected) return;

      const nextPath = [...path, selected];
      setPath(nextPath);
      setQuery("");

      try {
        const children = await requestChildren(selected.id);

        if (children.length === 0) {
          finalizeRegion(nextPath);
          setOpenRegion(false);
          return;
        }

        setOptions(children);
      } catch {
        finalizeRegion(nextPath);
        setOpenRegion(false);
      }
    },
    [options, path, requestChildren, finalizeRegion, setOpenRegion]
  );

  const zipLabel = labels?.zipLabel ?? t("websiteSuggest.zipCodeLabel");
  const zipPlaceholder =
    labels?.zipPlaceholder ?? t("websiteSuggest.zipCodePlaceholder");
  const zipHint = labels?.zipHint ?? t("websiteSuggest.zipCodeHint");
  const regionLabelText = labels?.regionLabel ?? t("websiteSuggest.regionLabel");
  const regionPlaceholder =
    labels?.regionPlaceholder ?? t("websiteSuggest.regionPlaceholder");
  const regionHint = labels?.regionHint ?? t("websiteSuggest.regionHint");

  const regionLabel = useMemo(() => {
    return formData.regionName || regionPlaceholder;
  }, [formData.regionName, regionPlaceholder]);
  const selectedPathNames = Array.isArray(formData.regionPathNames)
    ? formData.regionPathNames
    : [];
  const regionPathDisplay =
    selectedPathNames.length > 0
      ? selectedPathNames
      : formData.regionName
        ? [formData.regionName]
        : [];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="zipCode">{renderRequiredLabel(zipLabel)}</Label>
        <Input
          id="zipCode"
          inputMode="numeric"
          pattern="\d*"
          maxLength={5}
          placeholder={zipPlaceholder}
          value={formData.zipCode}
          onChange={(e) => onZipCodeChange(e.target.value)}
        />

        <p className="text-xs text-muted">{zipHint}</p>
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-semibold text-secondary uppercase tracking-wide">
          {renderRequiredLabel(regionLabelText)}
        </Label>

        <Popover
          open={openRegion}
          onOpenChange={(open) => {
            if (!open) {
              if (path.length > 0) finalizeRegion(path);
            }
            setOpenRegion(open);
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              className="w-full justify-between"
            >
              {regionLabel}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>

          <PopoverContent className="w-[360px] p-0">
            <div className="border-b p-2 space-y-1">
              {path.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={handleBack}
                >
                  ← {t("websiteSuggest.back")}
                </Button>
              )}

              {breadcrumb && (
                <div className="text-xs opacity-70 px-2">{breadcrumb}</div>
              )}

              {(formData.regionId || formData.regionName) && (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={handleClear}
                >
                  {t("websiteSuggest.clearSelection")}
                </Button>
              )}

              <Button
                type="button"
                variant="ghost"
                className="w-full justify-start"
                onClick={handleUnknown}
              >
                {t("websiteSuggest.unknown")}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full justify-start"
                onClick={handleDone}
              >
                {t("websiteSuggest.done")}
              </Button>
            </div>

            <Command
              shouldFilter={!(path.length === 0 && query.trim().length > 0)}
            >
              <CommandInput
                placeholder={regionPlaceholder}
                value={query}
                onValueChange={setQuery}
              />
              <CommandList>
                <CommandEmpty>
                  {failed
                    ? t("websiteSuggest.regionLoadFailed")
                    : loading
                    ? t("websiteSuggest.loading")
                    : t("websiteSuggest.noRegionFound")}
                </CommandEmpty>

                <CommandGroup>
                  {options.map((opt) => (
                    <CommandItem
                      key={opt.id}
                      value={opt.id}
                      onSelect={handleSelect}
                    >
                      <span className="truncate">{formatRegionLabel(opt.name, opt.nameEn)}</span>
                      <span className="ml-auto text-xs opacity-60">{">"}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <p className="text-xs text-muted">{regionHint}</p>

        {regionPathDisplay.length > 0 && (
          <div className="text-sm text-secondary">
            <span className="font-semibold">
              {t("websiteSuggest.regionPathPrefix") || "Region:"}
            </span>{" "}
            {regionPathDisplay.join(" > ")}
          </div>
        )}
      </div>
    </div>
  );
}
